// pages/NotasCreditoDebito.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import { obtenerFechaLocal } from '../utils/dateUtils';
import { useFiscal } from '../hooks/useFiscal';

const TIPOS_NCF = {
  '03': 'Nota de Débito (B03)',
  '04': 'Nota de Crédito (B04)',
  '33': 'Nota de Débito Electrónica (E33)',
  '34': 'Nota de Crédito Electrónica (E34)'
};

const fmtMonto = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2 }).format(n || 0);
const fmtFecha = (d) => { if (!d) return '-'; const [y, m, day] = String(d).substring(0, 10).split('-'); return `${day}/${m}/${y}`; };

const ITEM_VACIO = { descripcion: '', cantidad: 1, precio_unitario: 0 };

export default function NotasCreditoDebito() {
  const navigate = useNavigate();
  const fiscal = useFiscal();

  const [notas,    setNotas]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [clientes, setClientes] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [filtro,   setFiltro]   = useState({ clase: '', estado: '' });

  const [form, setForm] = useState({
    clase: 'nota_credito',
    serie: 'B',
    factura_referencia_id: '',
    ClienteId: '',
    fecha_emision: obtenerFechaLocal(),
    motivo_nota: '',
    observaciones: '',
    ncf: '',
    items: [{ ...ITEM_VACIO }]
  });

  const cargarNotas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro.clase)  params.set('clase', filtro.clase);
      if (filtro.estado) params.set('estado', filtro.estado);
      const { data } = await api.get(`/notas?${params}`);
      setNotas(data.notas || []);
    } catch (e) {
      Swal.fire('Error', 'No se pudieron cargar las notas', 'error');
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { cargarNotas(); }, [cargarNotas]);

  useEffect(() => {
    api.get('/clientes').then(r => setClientes(r.data?.clientes || r.data || [])).catch(() => {});
    api.get('/facturas?estado=emitida').then(r => setFacturas(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // ─── Formulario ───
  const setFormField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [k]: v };
    return { ...f, items };
  });
  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, { ...ITEM_VACIO }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  // Al seleccionar factura referencia, autocompletar cliente, serie, NCF e items
  const handleSelectFactura = (facturaId) => {
    setFormField('factura_referencia_id', facturaId);
    if (facturaId) {
      const f = facturas.find(f => String(f.id) === String(facturaId));
      if (f) {
        setFormField('ClienteId', String(f.ClienteId));
        const serieDetectada = f.ncf?.startsWith('E') ? 'E' : 'B';
        setFormField('serie', serieDetectada);
        if (f.ncf) setFormField('ncf', f.ncf);
        if (f.items?.length) {
          setFormField('items', f.items.map(i => ({
            descripcion:     i.descripcion || '',
            cantidad:        i.cantidad || 1,
            precio_unitario: i.precio_unitario || 0
          })));
        }
      }
    } else {
      setFormField('serie', 'B');
      setFormField('ncf', '');
      setFormField('items', [{ ...ITEM_VACIO }]);
    }
  };

  const subtotal = form.items.reduce((s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio_unitario || 0), 0);
  const itbis    = subtotal * ((fiscal.impuesto_tasa ?? 18) / 100);
  const total    = subtotal + itbis;

  const handleGuardar = async () => {
    if (!form.ClienteId) return Swal.fire('Atención', 'Selecciona un cliente', 'warning');
    if (!form.motivo_nota) return Swal.fire('Atención', 'Ingresa el motivo de la nota', 'warning');
    if (form.items.some(i => !i.descripcion)) return Swal.fire('Atención', 'Todos los ítems deben tener descripción', 'warning');

    try {
      const tipoNcf = form.serie === 'E'
        ? (form.clase === 'nota_credito' ? '34' : '33')
        : (form.clase === 'nota_credito' ? '04' : '03');
      await api.post('/notas', {
        ...form,
        tipo_ncf: tipoNcf
      });
      setModal(false);
      setForm({ clase: 'nota_credito', serie: 'B', factura_referencia_id: '', ClienteId: '', fecha_emision: obtenerFechaLocal(), motivo_nota: '', observaciones: '', ncf: '', items: [{ ...ITEM_VACIO }] });
      await cargarNotas();
      Swal.fire({ icon: 'success', title: 'Nota creada', text: 'La nota fue creada en estado borrador', timer: 2000, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al guardar', 'error');
    }
  };

  const handleEmitir = async (id) => {
    const conf = await Swal.fire({ title: '¿Emitir nota?', text: 'Esta acción cambiará el estado a Emitida', icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, emitir', cancelButtonText: 'Cancelar', confirmButtonColor: '#0066cc' });
    if (!conf.isConfirmed) return;
    try {
      await api.post(`/notas/${id}/emitir`);
      cargarNotas();
      Swal.fire({ icon: 'success', title: 'Nota emitida', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al emitir', 'error');
    }
  };

  const handleAnular = async (id) => {
    const { value } = await Swal.fire({ title: 'Anular nota', input: 'textarea', inputLabel: 'Motivo de anulación', inputPlaceholder: 'Explica el motivo...', showCancelButton: true, confirmButtonText: 'Anular', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545' });
    if (!value) return;
    try {
      await api.post(`/notas/${id}/anular`, { motivo: value });
      cargarNotas();
      Swal.fire({ icon: 'success', title: 'Nota anulada', timer: 1800, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'Error al anular', 'error');
    }
  };

  // ─── Badges ───
  const badgeClase = (clase) => {
    if (clase === 'nota_credito') return <span style={{ ...sx.badge, background: '#d1fae5', color: '#065f46' }}>NC</span>;
    return <span style={{ ...sx.badge, background: '#fee2e2', color: '#991b1b' }}>ND</span>;
  };
  const badgeEstado = (e) => {
    const m = { borrador: ['#f3f4f6','#4b5563'], emitida: ['#dbeafe','#1e40af'], anulada: ['#fee2e2','#991b1b'] };
    const [bg, color] = m[e] || m.borrador;
    return <span style={{ ...sx.badge, background: bg, color }}>{e}</span>;
  };

  return (
    <div style={sx.page}>
      {/* Header */}
      <div style={sx.header}>
        <div>
          <button onClick={() => navigate('/facturas')} style={sx.btnVolver}>← Volver</button>
          <h1 style={sx.titulo}>Notas de Crédito y Débito</h1>
          <p style={sx.subtitulo}>Gestión de ajustes y correcciones a facturas emitidas</p>
        </div>
        <button onClick={() => setModal(true)} style={sx.btnPrimario}>+ Nueva Nota</button>
      </div>

      {/* KPIs */}
      <div style={sx.kpiGrid}>
        <KpiCard icono="✅" titulo="Notas de Crédito" valor={notas.filter(n => n.clase === 'nota_credito').length} color="#059669" bg="#f0fdf4" />
        <KpiCard icono="📈" titulo="Notas de Débito"  valor={notas.filter(n => n.clase === 'nota_debito').length} color="#dc2626" bg="#fef2f2" />
        <KpiCard icono="⏳" titulo="En Borrador"       valor={notas.filter(n => n.estado === 'borrador').length}  color="#f59e0b" bg="#fffbeb" />
        <KpiCard icono="📋" titulo="Emitidas"          valor={notas.filter(n => n.estado === 'emitida').length}   color="#0066cc" bg="#eff6ff" />
      </div>

      {/* Filtros */}
      <div style={sx.filtrosRow}>
        <select value={filtro.clase} onChange={e => setFiltro(f => ({ ...f, clase: e.target.value }))} style={sx.select}>
          <option value="">Todos los tipos</option>
          <option value="nota_credito">Notas de Crédito</option>
          <option value="nota_debito">Notas de Débito</option>
        </select>
        <select value={filtro.estado} onChange={e => setFiltro(f => ({ ...f, estado: e.target.value }))} style={sx.select}>
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="emitida">Emitida</option>
          <option value="anulada">Anulada</option>
        </select>
        <button onClick={cargarNotas} style={sx.btnSecundario}>🔄 Actualizar</button>
      </div>

      {/* Tabla */}
      <div style={sx.tablaWrap}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>⏳ Cargando...</div>
        ) : notas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📄</div>
            <p style={{ color: '#6c757d', fontWeight: 600 }}>No hay notas registradas</p>
            <button onClick={() => setModal(true)} style={{ ...sx.btnPrimario, marginTop: '0.75rem' }}>Crear primera nota</button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Tipo','NCF','Fecha','Cliente','Factura Ref.','Motivo','Total','Estado','Acciones'].map(h => (
                  <th key={h} style={sx.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {notas.map((n, i) => (
                <tr key={n.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={sx.td}>{badgeClase(n.clase)}</td>
                  <td style={{ ...sx.td, fontFamily: 'monospace', fontWeight: 600 }}>{n.ncf || '—'}</td>
                  <td style={sx.td}>{fmtFecha(n.fecha_emision)}</td>
                  <td style={sx.td}>{n.cliente?.nombre || '—'}</td>
                  <td style={sx.td}>{n.facturaReferencia ? `#${n.facturaReferencia.numero_factura}` : '—'}</td>
                  <td style={{ ...sx.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.motivo_nota || '—'}</td>
                  <td style={{ ...sx.td, textAlign: 'right', fontWeight: 600 }}>RD$ {fmtMonto(n.total)}</td>
                  <td style={sx.td}>{badgeEstado(n.estado)}</td>
                  <td style={sx.td}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      {n.estado === 'borrador' && (
                        <button onClick={() => handleEmitir(n.id)} style={sx.btnAccion('#0066cc')}>Emitir</button>
                      )}
                      {n.estado !== 'anulada' && (
                        <button onClick={() => handleAnular(n.id)} style={sx.btnAccion('#dc3545')}>Anular</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal crear nota */}
      {modal && (
        <div style={sx.overlay}>
          <div style={sx.modalBox}>
            <div style={sx.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>
                {form.clase === 'nota_credito' ? '✅ Nueva Nota de Crédito' : '📈 Nueva Nota de Débito'}
              </h2>
              <button onClick={() => setModal(false)} style={sx.btnCerrar}>✕</button>
            </div>

            <div style={sx.modalBody}>
              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Serie de Comprobante</label>
                  <select value={form.serie} onChange={e => setFormField('serie', e.target.value)} style={sx.input}>
                    <option value="B">Serie B — Tradicional</option>
                    <option value="E">Serie E — Electrónico (e-CF)</option>
                  </select>
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Tipo de Nota</label>
                  <select value={form.clase} onChange={e => setFormField('clase', e.target.value)} style={sx.input}>
                    <option value="nota_credito">
                      {form.serie === 'E' ? 'Nota de Crédito Electrónica (E34)' : 'Nota de Crédito (B04)'}
                    </option>
                    <option value="nota_debito">
                      {form.serie === 'E' ? 'Nota de Débito Electrónica (E33)' : 'Nota de Débito (B03)'}
                    </option>
                  </select>
                </div>
              </div>
              <div style={sx.campo}>
                <label style={sx.label}>Factura de Referencia (opcional)</label>
                <select value={form.factura_referencia_id} onChange={e => handleSelectFactura(e.target.value)} style={sx.input}>
                  <option value="">Sin factura de referencia</option>
                  {facturas.map(f => (
                    <option key={f.id} value={f.id}>#{f.numero_factura} – {f.cliente?.nombre} – RD$ {fmtMonto(f.total)}</option>
                  ))}
                </select>
              </div>

              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>Cliente *</label>
                  <select value={form.ClienteId} onChange={e => setFormField('ClienteId', e.target.value)} style={sx.input}>
                    <option value="">Seleccionar cliente</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Fecha</label>
                  <input type="date" value={form.fecha_emision} onChange={e => setFormField('fecha_emision', e.target.value)} style={sx.input} />
                </div>
              </div>

              <div style={sx.fila2col}>
                <div style={sx.campo}>
                  <label style={sx.label}>NCF (si ya tienes la secuencia)</label>
                  <input type="text" value={form.ncf} onChange={e => setFormField('ncf', e.target.value)} style={sx.input} placeholder="Ej: B0400000001" />
                </div>
                <div style={sx.campo}>
                  <label style={sx.label}>Motivo *</label>
                  <input type="text" value={form.motivo_nota} onChange={e => setFormField('motivo_nota', e.target.value)} style={sx.input} placeholder="Razón de la nota..." />
                </div>
              </div>

              {/* Ítems */}
              <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <label style={{ ...sx.label, margin: 0 }}>Ítems</label>
                  <button onClick={addItem} style={sx.btnSecundario}>+ Agregar ítem</button>
                </div>
                {form.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <input
                      placeholder="Descripción"
                      value={item.descripcion}
                      onChange={e => setItem(i, 'descripcion', e.target.value)}
                      style={{ ...sx.input, flex: 3 }}
                    />
                    <input
                      type="number" min="1" placeholder="Cant."
                      value={item.cantidad}
                      onChange={e => setItem(i, 'cantidad', e.target.value)}
                      style={{ ...sx.input, flex: 1 }}
                    />
                    <input
                      type="number" min="0" step="0.01" placeholder="Precio"
                      value={item.precio_unitario}
                      onChange={e => setItem(i, 'precio_unitario', e.target.value)}
                      style={{ ...sx.input, flex: 1.5 }}
                    />
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(i)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, color: '#dc3545', padding: '0.5rem', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                    )}
                  </div>
                ))}
              </div>

              {/* Totales */}
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '1rem', marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ color: '#6c757d' }}>Subtotal</span>
                  <span style={{ fontWeight: 600 }}>{fiscal.moneda?.simbolo || 'RD$'} {fmtMonto(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ color: '#6c757d' }}>{fiscal.impuesto_label || 'ITBIS'} ({fiscal.impuesto_tasa ?? 18}%)</span>
                  <span style={{ fontWeight: 600 }}>{fiscal.moneda?.simbolo || 'RD$'} {fmtMonto(itbis)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #dee2e6', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                  <span style={{ fontWeight: 700, color: '#1a1a2e' }}>TOTAL</span>
                  <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0066cc' }}>RD$ {fmtMonto(total)}</span>
                </div>
              </div>

              <div style={sx.campo}>
                <label style={sx.label}>Observaciones</label>
                <textarea value={form.observaciones} onChange={e => setFormField('observaciones', e.target.value)} style={{ ...sx.input, minHeight: 70, resize: 'vertical' }} placeholder="Notas adicionales..." />
              </div>
            </div>

            <div style={sx.modalFooter}>
              <button onClick={() => setModal(false)} style={sx.btnSecundario}>Cancelar</button>
              <button onClick={handleGuardar} style={sx.btnPrimario}>💾 Guardar Nota</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icono, titulo, valor, color, bg }) {
  return (
    <div style={{ background: bg, borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{icono}</div>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{titulo}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#1a1a2e' }}>{valor}</div>
    </div>
  );
}

const sx = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' },
  titulo: { fontSize: '2rem', fontWeight: 700, color: '#1a1a2e', margin: '0.5rem 0 0.25rem 0' },
  subtitulo: { color: '#6c757d', fontSize: '0.95rem', margin: 0 },
  btnVolver: { background: 'none', border: 'none', color: '#0066cc', cursor: 'pointer', fontSize: '0.9rem', padding: 0, fontWeight: 600 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  filtrosRow: { display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '0.55rem 0.9rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.9rem', background: '#fff', cursor: 'pointer' },
  btnPrimario: { background: '#0066cc', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.4rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' },
  btnSecundario: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '0.55rem 1.1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
  btnAccion: (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 5, padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }),
  tablaWrap: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflowX: 'auto' },
  th: { background: '#f8f9fa', padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' },
  td: { padding: '0.7rem 1rem', color: '#495057', borderBottom: '1px solid #f1f3f5', fontSize: '0.875rem' },
  badge: { display: 'inline-block', padding: '0.25rem 0.65rem', borderRadius: 50, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modalBox: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  modalBody: { padding: '1.5rem' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' },
  btnCerrar: { background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6c757d', padding: '0.25rem' },
  fila2col: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  campo: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' },
  label: { fontSize: '0.875rem', fontWeight: 600, color: '#495057' },
  input: { padding: '0.6rem 0.75rem', border: '1px solid #dee2e6', borderRadius: 6, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }
};
